"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";

import {
  StandardFormInput,
  StandardFormSelect,
} from "@/components/common/StandardFormInput";
import { Switch } from "@/components/ui/switch";
import {
  MessageSquare,
  ShoppingCart,
  Clock,
  User,
  Phone,
  IndianRupee,
  X,
  Mail,
  Send,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { ActionButton } from "@/components/common/DataViewTypes";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchAbandonedCartCustomersRequest,
  createAbandonedCartBulkMessageRequest,
} from "@/store/slices/abandonedCart.slice";
import { AbandonedCartCustomer } from "@/types/models/marketing";

const AbandonedCartPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm: _confirm } = useConfirmation();

  const {
    customers,
    customersLoaded,
    customersLoading,
    customersError,
    customersPagination,
    formState,
  } =
    useSelector((state: RootState) => state.abandonedCart);
  const [pageIndex, setPageIndex] = useState(0);

  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageTitle, setMessageTitle] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [sendImmediately, setSendImmediately] = useState(true);

  const fetchCustomers = useCallback(() => {
    dispatch(fetchAbandonedCartCustomersRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  useEffect(() => {
    if (customersLoading) return;

    const hasNotFoundError = customersError?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (customersPagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!customersLoaded && (customers?.length || 0) === 0) {
      fetchCustomers();
      return;
    }

    if (customersLoaded && hasPageMismatch) {
      fetchCustomers();
    }
  }, [
    customers?.length,
    customersLoaded,
    customersLoading,
    customersError?.status,
    customersPagination?.page,
    pageIndex,
    fetchCustomers,
  ]);

  useEffect(() => {
    if (formState.success && !formState.loading) {
      setMessageTitle("");
      setMessageContent("");
      setIsMessageModalOpen(false);
    }
  }, [formState.success, formState.loading]);

  const handleSendBulkMessage = () => {
    setIsMessageModalOpen(true);
  };

  const handleRefresh = () => {
    fetchCustomers();
  };

  const handleSendMessageSubmit = async () => {
    if (messageContent.trim() && messageTitle.trim()) {
      dispatch(
        createAbandonedCartBulkMessageRequest({
          title: messageTitle,
          content: messageContent,
          channel: channel,
          send_immediately: sendImmediately,
        }),
      );
    }
  };

  const columns: ColumnDef<AbandonedCartCustomer>[] = [
    {
      accessorKey: "customer_name",
      header: "Customer",
      minSize: 250,
      size: 300,
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {customer.customer_name}
              </p>
              <div className="text-sm text-muted-foreground space-y-0.5">
                <p>{customer.customer_email}</p>
                {customer.customer_phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {customer.customer_phone}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "cart_value",
      header: "Cart Value",
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="text-sm">
            <div className="flex items-center gap-1 font-medium">
              <IndianRupee className="h-3 w-3" />
              {customer.cart_value.toFixed(2)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "items_count",
      header: "Items",
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="text-sm">
            <div className="flex items-center gap-1 font-medium">
              <ShoppingCart className="h-3 w-3" />
              {customer.items_count}
            </div>
            <p className="text-muted-foreground">items</p>
          </div>
        );
      },
    },
    {
      accessorKey: "abandoned_at",
      header: "Abandoned",
      cell: ({ row }) => {
        const customer = row.original;
        const abandonedDate = new Date(customer.abandoned_at);
        const daysSince = Math.floor(
          (Date.now() - abandonedDate.getTime()) / (1000 * 60 * 60 * 24),
        );

        return (
          <div className="text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {abandonedDate.toLocaleDateString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {daysSince} {daysSince === 1 ? "day" : "days"} ago
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "last_activity",
      header: "Last Activity",
      cell: ({ row }) => {
        return (
          <div className="text-sm text-muted-foreground">
            {new Date(row.original.last_activity).toLocaleDateString()}
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${customersLoading ? "animate-spin" : ""}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: customersLoading,
    },
    {
      label: "Send Bulk Message",
      icon: <MessageSquare className="h-3 w-3" />,
      onClick: handleSendBulkMessage,
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Abandoned Carts"
            description="Recover lost sales by reaching out to customers who left items in their cart"
            columns={columns}
            data={customers}
            pagination={customersPagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={customersLoading}
            emptyStateType="cart"
            actionButtons={actionButtons}
            enableColumnResizing={true}
          />
        </div>
      </TableContainer>

      {/* Send Message Modal */}
      {isMessageModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="sticky top-0 bg-background rounded-t-lg z-10 flex items-center justify-between p-6 shadow-md dark:shadow-[0_4px_6px_-1px_rgb(0_0_0)]">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Send Bulk Message
                </h2>
                <p className="text-sm text-muted-foreground">
                  Send a message to all customers with abandoned carts
                </p>
              </div>
              <button
                onClick={() => setIsMessageModalOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Error Message */}
              {formState.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">
                    {formState.error.message}
                  </p>
                </div>
              )}

              {/* Message Details Card */}
              <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium text-foreground">
                  Message Details
                </h3>
                <StandardFormInput
                  label="Message Title"
                  type="text"
                  placeholder="Enter message title (e.g., 'Complete Your Purchase')"
                  value={messageTitle}
                  onChange={(e) => setMessageTitle(e.target.value)}
                />
                <div className="space-y-1">
                  <label className="block text-[13px] font-normal text-muted-foreground">
                    Message Content
                  </label>
                  <textarea
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder="Write your message to customers about their abandoned cart..."
                    rows={6}
                    className="w-full rounded-lg bg-background border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  />
                </div>
              </div>

              {/* Delivery Settings Card */}
              <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium text-foreground">
                  Delivery Settings
                </h3>
                <StandardFormSelect
                  label="Channel"
                  value={channel}
                  onValueChange={(value) =>
                    setChannel(value as "email" | "sms")
                  }
                  options={[
                    { label: "Email", value: "email" },
                    { label: "SMS", value: "sms" },
                  ]}
                />
                <div className="space-y-1">
                  <label className="block text-[13px] font-normal text-muted-foreground">
                    Delivery Timing
                  </label>
                  <div className="flex items-center justify-between bg-background rounded-lg px-4 py-2 h-[38px]">
                    <span className="text-sm text-foreground flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Send immediately
                    </span>
                    <Switch
                      checked={sendImmediately}
                      onCheckedChange={setSendImmediately}
                    />
                  </div>
                </div>
              </div>

              {/* Preview Card */}
              <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium text-foreground">Preview</h3>
                <div className="bg-background rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
                    {channel === "email" ? (
                      <Mail className="h-4 w-4 text-primary" />
                    ) : (
                      <MessageSquare className="h-4 w-4 text-primary" />
                    )}
                    <span className="text-xs text-muted-foreground uppercase font-medium">
                      {channel} Preview
                    </span>
                  </div>
                  {messageTitle && (
                    <h4 className="font-semibold text-foreground mb-2">
                      {messageTitle}
                    </h4>
                  )}
                  {messageContent ? (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {messageContent}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground/50 italic">
                      Your message content will appear here...
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="sticky bottom-0 bg-background rounded-b-lg z-10 flex items-center justify-end gap-3 p-6 shadow-[0_-6px_10px_-2px_rgb(0_0_0_/_0.15)] dark:shadow-[0_-6px_10px_-2px_rgb(0_0_0)]">
              <button
                type="button"
                onClick={() => setIsMessageModalOpen(false)}
                className="px-4 py-2 bg-secondary-background rounded hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendMessageSubmit}
                disabled={
                  formState.loading ||
                  !messageContent.trim() ||
                  !messageTitle.trim()
                }
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {formState.loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbandonedCartPage;
